import { Children, isValidElement, type ReactNode } from "react";
import type { MatchProps } from "./Match";

interface SwitchProps {
  /** Content to render when no `Match` conditions are truthy. */
  fallback?: ReactNode;
  /** `Match` components defining the cases to evaluate. */
  children?: ReactNode;
  /**
   * Value to match against `Match` children's `case` prop.
   * When provided, renders the first `Match` whose `case === value`.
   *
   * @example
   * <Switch value={step}>
   *   <Match case="intro"><IntroStep /></Match>
   *   <Match case="form"><FormStep /></Match>
   * </Switch>
   */
  value?: unknown;
}

/**
 * Renders the first `Match` child whose `when` condition is truthy.
 *
 * Use `Switch` + `Match` for mutually exclusive conditions (if/else-if/else pattern).
 * Evaluates `Match` children in order and renders only the first match.
 * If no conditions match, renders the fallback (or nothing).
 *
 * @example
 * // Status-based rendering
 * <Switch fallback={<Idle />}>
 *   <Match when={isLoading}>
 *     <Spinner />
 *   </Match>
 *   <Match when={error}>
 *     {(err) => <Error message={err.message} />}
 *   </Match>
 *   <Match when={data}>
 *     {(data) => <Content data={data} />}
 *   </Match>
 * </Switch>
 *
 * @example
 * // Value-based matching (like a switch statement)
 * <Switch value={step}>
 *   <Match case="intro"><IntroStep /></Match>
 *   <Match case="form"><FormStep /></Match>
 *   <Match case="confirm"><ConfirmStep /></Match>
 * </Switch>
 *
 * @see {@link Show} for simple single-condition rendering
 * @see {@link Match} for defining individual cases
 */
function Switch({ fallback, children, value }: SwitchProps): ReactNode {
  const childArray = Children.toArray(children);
  const isValueMode = value !== undefined;

  for (const child of childArray) {
    if (!isValidElement<MatchProps<unknown>>(child)) {
      continue;
    }

    const isMatch = isValueMode
      ? child.props.case === value
      : !!child.props.when;

    if (!isMatch) {
      continue;
    }

    const { when, children: matchChildren } = child.props;

    if (typeof matchChildren === "function") {
      return matchChildren(isValueMode ? value : when);
    }

    return matchChildren;
  }

  return fallback ?? null;
}

export default Switch;
